import { ReactNode, useState } from 'react'

import { Button } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, rem, ScrollArea } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'

type MainContentProps = {
  children: ReactNode
  step: number
  onContinue?: () => void
  desktopBanner?: string
  desktopBannerRatio: {
    width: number
    height: number
  }
  frisoModalRatio: {
    width: number
    height: number
  }
  mobileBanner?: string
  mobileBannerRatio: {
    width: number
    height: number
  }
  loading?: boolean
}

const MainContent = ({
  children,
  step,
  onContinue,
  desktopBanner,
  frisoModalRatio,
  mobileBanner,
  mobileBannerRatio,
  desktopBannerRatio,
  loading,
}: MainContentProps) => {
  const { formatMessage: f } = useIntl()
  const currentUrl = useCurrentUrl()
  const stickyActionCommonProps = {
    display: 'flex',
    gap: rem(8),
    justifyContent: 'center',
    background: 'white',
    paddingTop: rem(24),
    paddingBottom: rem(24),
    borderBottomRightRadius: rem(8),
    button: {
      width: '100%',
      maxWidth: rem(400),
    },
  }
  const stickyAction = (
    <>
      <Button
        loading={loading}
        data-event-category="Leadgen Pop Up"
        data-event-action="Submit Click"
        data-event-label={currentUrl}
        onClick={() => {
          isMobile && setIsFullScreen(true)
          onContinue?.()
        }}
      >
        {f({ id: 'onboarding.friso.continue' })}
      </Button>
    </>
  )

  const isMobile = useIsMobile()

  const { width: viewportWidth, height: viewportHeight } = useViewportSize()
  const curBannerHeight =
    (viewportWidth * mobileBannerRatio.height) / mobileBannerRatio.width
  const minHeightNha =
    (viewportHeight <= 667 ? 190 : 265) + curBannerHeight + 74
  const [isFullScreen, setIsFullScreen] = useState(false)

  return isMobile ? (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
      onTouchStart={() => setIsFullScreen(true)}
    >
      <Box
        className="mobile-template"
        sx={{
          position: 'absolute',
          height: `${
            (isFullScreen ? 1 : minHeightNha / viewportHeight) * 100
          }%`,
          left: 0,
          bottom: 0,
          right: 0,
          transition: 'height 0.4s ease',
        }}
      >
        <Box
          sx={{
            height: '100%',
            backgroundColor: 'white',
            borderTopLeftRadius: isFullScreen ? 0 : rem(12),
            borderTopRightRadius: isFullScreen ? 0 : rem(12),
          }}
        >
          <Box
            sx={{
              height: 'calc(100% - 74px)',
              background: `url(${mobileBanner})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'top center',
              overflowY: 'auto',
              borderRadius: 'inherit',
            }}
          >
            <AspectRatio
              ratio={mobileBannerRatio?.width / mobileBannerRatio?.height}
              w="100%"
            ></AspectRatio>
            <Box
              sx={{
                padding: `${rem(28)} ${rem(16)} ${rem(16)}`,
                background: 'white',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
      {step === 0 && (
        <Box
          sx={{
            ...stickyActionCommonProps,
            position: 'absolute',
            left: 0,
            bottom: 0,
            right: 0,
            padding: rem(16),
            // button: {
            //   width: `calc((100% - ${rem(24)})/2)`,
            // },
          }}
        >
          {stickyAction}
        </Box>
      )}
    </Box>
  ) : (
    <AspectRatio
      className="desktop-template"
      ratio={frisoModalRatio.width / frisoModalRatio.height}
      w="100%"
      sx={{
        '& > div': {
          justifyContent: 'flex-end',
        },
      }}
    >
      <Box
        sx={{
          background: `url(${desktopBanner})`,
          backgroundSize: 'contain',
          backgroundPosition: 'top left',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          height: '100%',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          borderRadius: rem(8),
          backgroundColor: 'white',
          boxShadow:
            '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 2.25rem 1.75rem -0.4375rem, rgba(0, 0, 0, 0.04) 0 1.0625rem 1.0625rem -0.4375rem',
        }}
      >
        {/* <img
          alt={f({
            id: 'onboarding.friso.heading',
            defaultMessage: 'Your information',
          })}
          src={desktopBanner}
          loading="lazy"
          style={{
            borderTopLeftRadius: rem(8),
            borderBottomLeftRadius: rem(8),
            height: '100%',
            display: 'block',
          }}
        /> */}
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            width: `${
              ((frisoModalRatio.width - desktopBannerRatio.width) /
                frisoModalRatio.width) *
              100
            }%`,
          }}
        >
          <ScrollArea h={'calc(100% - 90px)'}>
            <Box
              sx={{
                padding: `${rem(32)} ${rem(24)} 0`,
              }}
            >
              {children}
            </Box>
          </ScrollArea>
          {step === 0 && (
            <Box
              sx={{
                ...stickyActionCommonProps,
                position: 'absolute',
                left: 0,
                bottom: 0,
                right: 0,
              }}
            >
              {stickyAction}
            </Box>
          )}
        </Box>
      </Box>
    </AspectRatio>
  )
}

export default MainContent
